import React, { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "react-query";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, IconButton, LinearProgress, Paper, Tooltip } from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import Canvas from "../../navigation/canvas/Canvas";
import ErrorPopup from "../../common/ErrorPopup";
import IyoAuth from "../../../auth/Auth";
import OwnerEditDialog from "./OwnerEditDialog";
import OwnersService from "../../../apis/OwnersService";
import TableToolbar from "../../common/TableToolbar";
import { columns } from "./columns";
import { mockOwners } from "./mockOwnersData";

const Owners = ({}) => {
  // ####################[ Setup and Initialization ]####################
  const queryClient = useQueryClient();

  const initializeColumns = (initialColumns) => {
    return initialColumns.concat({
      field: "actions",
      headerName: "Actions",
      description: "Actions",
      minWidth: 175,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              onClick={(event) => handleEdit(event, params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          &nbsp;
          {params.row.disabled ? (
            <Tooltip title="Enable">
              <IconButton
                color="secondary"
                onClick={(event) => handleDisableSubmit(event, params.row)}
              >
                <VisibilityOffIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Disable">
              <IconButton
                color="primary"
                onClick={(event) => handleDisableSubmit(event, params.row)}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    });
  };

  // ####################[ State Definitions ]####################
  const [businessId, setBusinessId] = useState(null);
  const [cols, setCols] = useState(initializeColumns(columns));
  const [editOpen, setEditOpen] = useState(false);
  const [rowEditing, setRowEditing] = useState([]);
  const [errDisplayText, setErrDisplayText] = useState("");
  const [errorOpen, setErrorOpen] = useState(false);
  const [editMode, setEditMode] = useState("");
  const [timezoneName, setTimezoneName] = useState(null);

  // ####################[ Query Definitions ]####################
  const getOwners = useQuery(
    "getOwners",
    async () => {
      return await OwnersService.getAll();
      // return mockOwners;
    },
    {
      onError: (error) => {
        handleError(error);
      },
      retry: false,
      staleTime: Infinity,
    }
  );

  const postOwner = useMutation(
    "postOwner",
    async (data) => {
      return await OwnersService.post(data);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries("getOwners");
      },
      onError: (error) => {
        handleError(error);
      },
    }
  );

  const putOwner = useMutation(
    "putOwner",
    async (data) => {
      return await OwnersService.put(rowEditing.id, data);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries("getOwners");
      },
      onError: (error) => {
        handleError(error);
      },
    }
  );

  // ####################[ Event Handlers ]####################
  const handleCreate = async () => {
    setEditMode("create");
    setRowEditing(null);
    setBusinessId(null);
    setTimezoneName(null);
    setEditOpen(true);
  };

  const handleDisableSubmit = async (event, row) => {
    setRowEditing(row);
    const body = {
      id: row.id,
      disabled: row.disabled ? 0 : 1,
      updated_by: await IyoAuth.getUser(),
    };
    putOwner.mutate(body);
  };

  const handleEdit = async (event, row) => {
    setEditMode("edit");
    setRowEditing(row);
    setBusinessId(row.business_id);
    setTimezoneName(row.timezone);
    setEditOpen(true);
  };

  const handleError = (error) => {
    setErrorOpen(false);
    setErrDisplayText(
      `${error.message}: ` +
        `${
          error.response
            ? JSON.stringify(error.response?.data)
            : error.toString().slice(0, 200)
        }`
    );
    setErrorOpen(true);

    console.error(
      `${error.message}: ${JSON.stringify(error.response?.data || error)}`
    );
  };

  const refreshTable = async () => {
    console.info("Refreshing table");
    await queryClient.invalidateQueries("getOwners");
  };

  // ####################[ The Component ]####################
  return (
    <Box>
      <Canvas appName="nGEST" title="Owners">
        <TableToolbar editHandler={handleCreate} onRefresh={refreshTable} />
        <Paper sx={{ height: "77vh", "& .disabled": { color: "#b2b29b" } }}>
          <DataGridPro
            columns={cols}
            rows={getOwners.data || []}
            getRowClassName={(params) =>
              params.row.disabled ? "disabled" : ""
            }
            slots={{ loadingOverlay: LinearProgress, toolbar: GridToolbar }}
            loading={getOwners.isLoading}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  created_by: false,
                  created_datetime: false,
                  updated_by: false,
                  updated_datetime: false,
                },
              },
            }}
            density="compact"
          />
        </Paper>
      </Canvas>
      <OwnerEditDialog
        open={editOpen}
        setOpen={setEditOpen}
        rowEditing={rowEditing}
        mode={editMode}
        handleError={handleError}
      />
      <ErrorPopup
        open={errorOpen}
        setOpen={setErrorOpen}
        displayText={errDisplayText}
      />
    </Box>
  );
};

export default Owners;
